import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import PrepareModal from './PrepareModal'
import EditWorkout from './EditWorkout'
import AthletePanel from './AthletePanel'
import VideoModal from './VideoModal'
import SectionLogModal from './SectionLogModal'
import { canSeeWorkouts, hasClassAccess, hasPrivateTrainingAccess, isCoach as profileIsCoach } from '../utils/access'
import { formatPrescriptionValue } from '../utils/prescriptionTypes'

const TC = { 'Babes Who Fight Bears': 'track-bears', 'Strong & Savage': 'track-strength', 'Olympic Weightlifting': 'track-open' }
const RX = [{ e: '✋', k: 'highfive' }, { e: '🔥', k: 'fire' }, { e: '💪', k: 'strong' }]
const STRENGTH_TERMS = [
  'squat',
  'deadlift',
  'press',
  'bench',
  'snatch',
  'clean',
  'jerk',
  'pull',
  'row',
  'lunge',
  'hinge',
  'thruster',
  'carry'
]

function formatDate(d) { return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) }
function toISO(d) { return d.toISOString().split('T')[0] }

function parseScore(val, scoreType) {
  if (isMissValue(val)) return -Infinity
  if (!val) return -Infinity
  // For time: parse mm:ss into seconds
  if (scoreType === 'For Time') {
    const parts = (val || '').split(':')
    if (parts.length === 2) return -(parseInt(parts[0]) * 60 + parseInt(parts[1]))
    return -(parseFloat(val) || Infinity)
  }
  const num = parseFloat((val || '').replace(/[^\d.]/g, ''))
  return isNaN(num) ? -Infinity : num
}

function getBestScore(values, scoreType) {
  if (!values || values.length === 0) return null
  const valid = values.filter(value => value && !isMissValue(value))
  if (!valid.length) return null
  return valid.reduce((best, v) => parseScore(v, scoreType) > parseScore(best, scoreType) ? v : best, valid[0])
}

function formatSectionScore(log, scoreType) {
  if (!log) return null
  if (scoreType === 'AMRAP') {
    const parts = []
    if (log.rounds != null) parts.push(`${log.rounds} rounds`)
    if (log.reps != null) parts.push(`+ ${log.reps} reps`)
    return parts.join(' ') || null
  }
  return log.score || null
}

function sectionScoreForSort(log, scoreType) {
  if (!log) return null
  if (scoreType === 'AMRAP') {
    return `${String(log.rounds || 0).padStart(4, '0')}${String(log.reps || 0).padStart(4, '0')}`
  }
  return log.score || null
}

function isMissValue(value) {
  return /\bmiss\b/i.test(value || '')
}

function displaySetLogValue(value) {
  return (value || '').replace(/\s+-\s+(Made|Miss)$/i, '')
}

function formatSetLogValue(value, made) {
  const clean = (value || '').trim()
  if (!clean) return ''
  return `${clean} - ${made ? 'Made' : 'Miss'}`
}

function hasStrengthCue(section, movement) {
  const sectionType = (section?.type || '').toLowerCase()
  const scoreType = (section?.score_type || '').toLowerCase()
  const movementName = (movement?.name || '').toLowerCase()
  const movementNotes = (movement?.notes || '').toLowerCase()
  const sectionNotes = (section?.notes || '').toLowerCase()
  const sets = movement?.sets || []

  if (sectionType.includes('strength')) return true
  if (scoreType.includes('heaviest')) return true
  if (sets.some(set => (set.load || '').includes('%') || set.rpe)) return true
  if (/%|1rm|one rep max|percent/.test(`${movementNotes} ${sectionNotes}`)) return true
  return STRENGTH_TERMS.some(term => movementName.includes(term))
}

export default function Workouts({ user, profile }) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [workouts, setWorkouts] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [prepare, setPrepare] = useState(null)
  const [editing, setEditing] = useState(null)
  const [toast, setToast] = useState(null)
  const [announcement, setAnnouncement] = useState(null)
  const [editingAnnouncement, setEditingAnnouncement] = useState(false)
  const [announcementText, setAnnouncementText] = useState('')
  const [athletePanel, setAthletePanel] = useState(null)
  const [logModal, setLogModal] = useState(null)
  const [selectedTrack, setSelectedTrack] = useState('')
  const isCoach = profileIsCoach(profile)
  const hasWorkoutAccess = canSeeWorkouts(profile)

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 2500) }

  const deleteWorkout = async (workoutId) => {
    const fail = (label, error) => {
      if (!error) return false
      showToast(`${label}: ${error.message}`)
      return true
    }

    const { data: sections, error: sectionLoadError } = await supabase
      .from('workout_sections')
      .select('id, movements(id, sets(id))')
      .eq('workout_id', workoutId)

    if (fail('Could not load workout pieces', sectionLoadError)) return

    const sectionIds = (sections || []).map(section => section.id)
    const movementIds = (sections || []).flatMap(section => (section.movements || []).map(movement => movement.id))
    const setIds = (sections || []).flatMap(section =>
      (section.movements || []).flatMap(movement => (movement.sets || []).map(set => set.id))
    )

    const { data: results, error: resultLoadError } = await supabase
      .from('results')
      .select('id')
      .eq('workout_id', workoutId)

    if (fail('Could not load workout results', resultLoadError)) return

    const resultIds = (results || []).map(result => result.id)

    if (resultIds.length) {
      const { error } = await supabase.from('reactions').delete().in('result_id', resultIds)
      if (fail('Could not delete reactions', error)) return
    }

    const deleteSteps = [
      () => supabase.from('set_logs').delete().eq('workout_id', workoutId),
      () => supabase.from('section_logs').delete().eq('workout_id', workoutId),
      () => supabase.from('program_workouts').delete().eq('workout_id', workoutId),
      () => resultIds.length ? supabase.from('results').delete().in('id', resultIds) : Promise.resolve({ error: null }),
      () => setIds.length ? supabase.from('sets').delete().in('id', setIds) : Promise.resolve({ error: null }),
      () => movementIds.length ? supabase.from('movements').delete().in('id', movementIds) : Promise.resolve({ error: null }),
      () => sectionIds.length ? supabase.from('workout_sections').delete().in('id', sectionIds) : Promise.resolve({ error: null })
    ]

    for (const step of deleteSteps) {
      const { error } = await step()
      if (fail('Delete failed', error)) return
    }

    const { data: deletedRows, error: workoutDeleteError } = await supabase
      .from('workouts')
      .delete()
      .eq('id', workoutId)
      .select('id')

    if (fail('Could not delete workout', workoutDeleteError)) return

    if (!deletedRows?.length) {
      const { data: archivedRows, error: archiveError } = await supabase
        .from('workouts')
        .update({
          date: null,
          title: '[Deleted] Workout',
          notes: 'Deleted from the app by a coach.'
        })
        .eq('id', workoutId)
        .select('id')

      if (archiveError || !archivedRows?.length) {
        const { data: movedRows, error: moveError } = await supabase
          .from('workouts')
          .update({
            date: '1900-01-01',
            title: '[Deleted] Workout',
            notes: 'Deleted from the app by a coach.'
          })
          .eq('id', workoutId)
          .select('id')

        if (fail('Could not hide workout', moveError)) return
        if (!movedRows?.length) {
          showToast('Workout pieces were deleted, but Supabase would not remove or hide the main workout row.')
          return
        }
      }
    }

    showToast(deletedRows?.length ? 'Workout deleted' : 'Workout hidden')
    setWorkouts(prev => prev.filter(workout => workout.id !== workoutId))
  }

  const fetchWorkouts = useCallback(async () => {
    setLoading(true)
    const membershipType = profile?.membership_type
    const PUBLIC_TRACKS = ['Babes Who Fight Bears', 'Strong & Savage', 'Olympic Weightlifting']

    if (!hasWorkoutAccess) {
      setWorkouts([])
      setLoading(false)
      return
    }

    let query = supabase
      .from('workouts')
      .select(`
        *,
        workout_sections(
          *,
          section_logs(*, profiles(name, avatar_url)),
          movements(
            *,
            sets(*,
              set_logs(*, profiles(name, avatar_url))
            )
          )
        ),
        results(*, profiles(name, avatar_url), reactions(*))
      `)
      .eq('date', toISO(currentDate))
      .not('date', 'is', null)
      .order('id', { ascending: false })

    // Filter by membership type for non-coaches
    if (!isCoach) {
      if (membershipType === 'Both') {
        // Public tracks + private workouts
        query = query.or(`track.in.(${PUBLIC_TRACKS.map(t => `"${t}"`).join(',')}),assigned_athlete_id.eq.${user.id}`)
      } else if (hasClassAccess(profile)) {
        // Public tracks only
        query = query.in('track', PUBLIC_TRACKS)
      } else if (hasPrivateTrainingAccess(profile)) {
        // Private track only — assigned workouts
        query = query.eq('assigned_athlete_id', user.id)
      } else {
        // No membership — return nothing
        setWorkouts([])
        setLoading(false)
        return
      }
    }

    const { data } = await query
    setWorkouts(data || [])
    setExpandedId(null)
    setLoading(false)
  }, [currentDate, hasWorkoutAccess, isCoach, profile, profile?.membership_type, user.id])

  useEffect(() => { fetchWorkouts() }, [fetchWorkouts])

  useEffect(() => {
    supabase.from('announcements').select('*').eq('active', true).order('created_at', { ascending: false }).limit(1)
      .then(({ data }) => { if (data?.[0]) setAnnouncement(data[0]) })
  }, [])

  const saveAnnouncement = async () => {
    if (!announcementText.trim()) return
    // Deactivate old announcements
    await supabase.from('announcements').update({ active: false }).eq('active', true)
    const { data } = await supabase.from('announcements')
      .insert({ message: announcementText.trim(), created_by: user.id, active: true })
      .select().single()
    if (data) { setAnnouncement(data); setEditingAnnouncement(false); setAnnouncementText('') }
  }

  const deleteAnnouncement = async () => {
    if (!announcement) return
    await supabase.from('announcements').update({ active: false }).eq('id', announcement.id)
    setAnnouncement(null)
  }

  const prevDay = () => { const d = new Date(currentDate); d.setDate(d.getDate() - 1); setCurrentDate(d) }
  const nextDay = () => { const d = new Date(currentDate); d.setDate(d.getDate() + 1); setCurrentDate(d) }
  const goToday = () => setCurrentDate(new Date())
  const isToday = toISO(currentDate) === toISO(new Date())
  const isFuture = toISO(currentDate) > toISO(new Date())

  const trackOptions = useMemo(() => {
    const tracks = []
    workouts.forEach(workout => {
      if (workout.track && !tracks.includes(workout.track)) tracks.push(workout.track)
    })
    return tracks
  }, [workouts])

  const activeTrack = selectedTrack || (isCoach ? 'all' : trackOptions[0] || '')

  const visibleWorkouts = useMemo(() => {
    if (!activeTrack || activeTrack === 'all') return workouts
    return workouts.filter(workout => workout.track === activeTrack)
  }, [activeTrack, workouts])

  useEffect(() => {
    if (!trackOptions.length) {
      if (selectedTrack) setSelectedTrack('')
      return
    }
    if (selectedTrack === 'all' && isCoach) return
    if (!selectedTrack || !trackOptions.includes(selectedTrack)) {
      setSelectedTrack(isCoach ? 'all' : trackOptions[0])
    }
  }, [isCoach, selectedTrack, trackOptions])

  useEffect(() => {
    if (!expandedId) return
    if (!visibleWorkouts.some(workout => workout.id === expandedId)) {
      setExpandedId(null)
    }
  }, [expandedId, visibleWorkouts])

  // Auto-create a results row so reactions work for new set_logs system
  const ensureResultRow = async (workoutId) => {
    const { data } = await supabase.from('results').upsert(
      { workout_id: workoutId, athlete_id: user.id, score: 'logged' },
      { onConflict: 'workout_id,athlete_id' }
    ).select('*, profiles(name, avatar_url), reactions(*)').single()
    return data
  }

  const ensureLocalResult = (workout, resultRow) => {
    if (!resultRow) return workout.results || []
    const results = workout.results || []
    if (results.some(result => result.id === resultRow.id || result.athlete_id === resultRow.athlete_id)) {
      return results.map(result => result.id === resultRow.id || result.athlete_id === resultRow.athlete_id ? { ...result, ...resultRow } : result)
    }
    return [...results, resultRow]
  }

  const updateSetLogsInState = (workoutId, movementId, savedLogs, resultRow) => {
    const bySetId = new Map(savedLogs.map(log => [log.set_id, log]))
    setWorkouts(prev => prev.map(workout => {
      if (workout.id !== workoutId) return workout
      return {
        ...workout,
        results: ensureLocalResult(workout, resultRow),
        workout_sections: (workout.workout_sections || []).map(section => ({
          ...section,
          movements: (section.movements || []).map(movement => {
            if (movement.id !== movementId) return movement
            return {
              ...movement,
              sets: (movement.sets || []).map(set => {
                const saved = bySetId.get(set.id)
                if (!saved) return set
                const nextLogs = [
                  ...(set.set_logs || []).filter(log => log.athlete_id !== user.id),
                  {
                    ...saved,
                    profiles: {
                      name: profile?.name,
                      avatar_url: profile?.avatar_url
                    }
                  }
                ]
                return { ...set, set_logs: nextLogs }
              })
            }
          })
        }))
      }
    }))
  }

  const logMovementSets = async (workoutId, movementId, rows) => {
    const payload = rows
      .filter(row => row.value.trim())
      .map(row => ({
        set_id: row.setId,
        movement_id: movementId,
        workout_id: workoutId,
        athlete_id: user.id,
        value: formatSetLogValue(row.value, row.made)
      }))

    if (!payload.length) {
      showToast('Add at least one set value')
      return false
    }

    const { data, error } = await supabase
      .from('set_logs')
      .upsert(payload, { onConflict: 'set_id,athlete_id' })
      .select('*')

    if (error) {
      showToast('Error: ' + error.message)
      return false
    }

    const resultRow = await ensureResultRow(workoutId)
    updateSetLogsInState(workoutId, movementId, data || payload, resultRow)
    showToast('Sets logged')
    return true
  }

  const logSection = async (section, workoutId, { rows, sectionLog }) => {
    const setPayload = rows
      .filter(row => row.value.trim())
      .map(row => ({
        set_id: row.setId,
        movement_id: row.movementId,
        workout_id: workoutId,
        athlete_id: user.id,
        value: formatSetLogValue(row.value, row.made)
      }))

    let savedSetLogs = []
    if (setPayload.length) {
      const { data, error } = await supabase.from('set_logs')
        .upsert(setPayload, { onConflict: 'set_id,athlete_id' })
        .select('*')
      if (error) { showToast('Error: ' + error.message); return false }
      savedSetLogs = data || setPayload
    }

    const hasSectionLog = sectionLog.score != null || sectionLog.rounds != null || sectionLog.reps != null || sectionLog.notes
    let savedSectionLog = null
    if (hasSectionLog) {
      const { data, error } = await supabase.from('section_logs').upsert(
        { section_id: section.id, workout_id: workoutId, athlete_id: user.id, ...sectionLog },
        { onConflict: 'section_id,athlete_id' }
      ).select('*').single()
      if (error) { showToast('Error: ' + error.message); return false }
      savedSectionLog = data
    }

    if (!savedSetLogs.length && !savedSectionLog) {
      showToast('Add a result or note first')
      return false
    }

    const resultRow = await ensureResultRow(workoutId)
    const logsByMovement = savedSetLogs.reduce((groups, log) => {
      groups[log.movement_id] = [...(groups[log.movement_id] || []), log]
      return groups
    }, {})
    Object.entries(logsByMovement).forEach(([movementId, logs]) => updateSetLogsInState(workoutId, movementId, logs, resultRow))
    if (savedSectionLog) updateSectionLogInState(workoutId, section.id, savedSectionLog, resultRow)
    showToast(`${section.type} logged`)
    return true
  }

  const updateSectionLogInState = (workoutId, sectionId, savedLog, resultRow) => {
    setWorkouts(prev => prev.map(workout => {
      if (workout.id !== workoutId) return workout
      return {
        ...workout,
        results: ensureLocalResult(workout, resultRow),
        workout_sections: (workout.workout_sections || []).map(section => {
          if (section.id !== sectionId) return section
          const currentLogs = section.section_logs || []
          return {
            ...section,
            section_logs: [
              ...currentLogs.filter(log => log.athlete_id !== user.id),
              {
                ...savedLog,
                profiles: {
                  name: profile?.name,
                  avatar_url: profile?.avatar_url
                }
              }
            ]
          }
        })
      }
    }))
  }

  const logSectionScore = async (sectionId, workoutId, payload) => {
    const { data, error } = await supabase.from('section_logs').upsert(
      { section_id: sectionId, workout_id: workoutId, athlete_id: user.id, ...payload },
      { onConflict: 'section_id,athlete_id' }
    ).select('*').single()
    if (!error) {
      const resultRow = await ensureResultRow(workoutId)
      updateSectionLogInState(workoutId, sectionId, data || { section_id: sectionId, workout_id: workoutId, athlete_id: user.id, ...payload }, resultRow)
      showToast('Logged!')
    }
    else showToast('Error: ' + error.message)
  }

  const toggleReaction = async (resultId, type, hasReacted) => {
    if (hasReacted) {
      await supabase.from('reactions').delete().match({ result_id: resultId, athlete_id: user.id, type })
    } else {
      // One reaction per athlete per result. Choosing a new emoji replaces the old one.
      await supabase.from('reactions').delete().match({ result_id: resultId, athlete_id: user.id })
      await supabase.from('reactions').insert({ result_id: resultId, athlete_id: user.id, type })
    }
    fetchWorkouts()
  }

  const getStrengthMovements = (workout) => {
    const sections = [...(workout.workout_sections || [])].sort((a, b) => a.order_index - b.order_index)
    const seen = new Set()
    const strengthMovements = []

    sections.forEach(section => {
      ;[...(section.movements || [])]
        .sort((a, b) => a.order_index - b.order_index)
        .forEach(movement => {
          const sets = [...(movement.sets || [])].sort((a, b) => a.order_index - b.order_index)
          if (!movement.name || !sets.length || !hasStrengthCue(section, { ...movement, sets })) return

          const key = `${movement.name.toLowerCase()}-${section.id}`
          if (seen.has(key)) return
          seen.add(key)

          strengthMovements.push({
            name: movement.name,
            sectionType: section.type,
            sets
          })
        })
    })

    if (strengthMovements.length) return strengthMovements

    return sections
      .flatMap(section => [...(section.movements || [])]
        .sort((a, b) => a.order_index - b.order_index)
        .map(movement => ({
          name: movement.name,
          sectionType: section.type,
          sets: [...(movement.sets || [])].sort((a, b) => a.order_index - b.order_index)
        }))
      )
      .filter(movement => movement.name && movement.sets.length)
  }

  return (
    <div>
      {/* Announcement banner */}
      {announcement && (
        <div style={{ background: 'rgba(200,169,106,0.08)', border: '1px solid var(--gold-dark)', borderRadius: '4px', padding: '10px 14px', marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <span style={{ fontSize: '16px', flexShrink: 0 }}>📣</span>
          <div style={{ flex: 1, fontSize: '14px', color: 'var(--bone)', lineHeight: 1.6 }}>{announcement.message}</div>
          {isCoach && <button onClick={deleteAnnouncement} style={{ background: 'none', border: 'none', color: 'var(--charcoal-light)', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}>×</button>}
        </div>
      )}

      {/* Coach announcement editor */}
      {isCoach && (
        <div style={{ marginBottom: '1rem' }}>
          {editingAnnouncement
            ? <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <textarea value={announcementText} onChange={e => setAnnouncementText(e.target.value)}
                  placeholder="Write an announcement for all athletes..."
                  style={{ flex: 1, minHeight: '60px', background: 'rgba(245,240,232,0.06)', border: '1px solid var(--gold-dark)', borderRadius: '2px', padding: '8px 10px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '14px', outline: 'none', resize: 'vertical' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <button className="btn-sm" onClick={saveAnnouncement} disabled={!announcementText.trim()}>Post</button>
                  <button className="btn-ghost" onClick={() => setEditingAnnouncement(false)} style={{ fontSize: '11px' }}>Cancel</button>
                </div>
              </div>
            : <button className="btn-ghost" style={{ fontSize: '11px', width: '100%' }}
                onClick={() => { setEditingAnnouncement(true); setAnnouncementText(announcement?.message || '') }}>
                {announcement ? '✏️ Edit Announcement' : '📣 Post Announcement'}
              </button>
          }
        </div>
      )}

      <div className="date-nav">
        <button className="date-nav-btn" onClick={prevDay}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div className="date-nav-label">{formatDate(currentDate)}</div>
          {!isToday && <div className="date-nav-today" onClick={goToday}>Back to today</div>}
        </div>
        <button className="date-nav-btn" onClick={nextDay}>›</button>
      </div>

      {/* Date picker + Today */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '1rem', alignItems: 'center' }}>
        <input type="date" value={toISO(currentDate)}
          onChange={e => { if (e.target.value) setCurrentDate(new Date(e.target.value + 'T12:00:00')) }}
          style={{ background: 'rgba(245,240,232,0.06)', border: '1px solid var(--border)', borderRadius: '2px', padding: '6px 10px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '14px', outline: 'none', cursor: 'pointer' }} />
        {!isToday && <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={goToday}>Today</button>}
      </div>

      {trackOptions.length > 1 && (
        <div className="track-select-row">
          <label htmlFor="workout-track-select">Track</label>
          <select
            id="workout-track-select"
            value={activeTrack}
            onChange={e => setSelectedTrack(e.target.value)}
          >
            {isCoach && <option value="all">All Tracks</option>}
            {trackOptions.map(track => (
              <option key={track} value={track}>{track}</option>
            ))}
          </select>
        </div>
      )}

      {loading && <div className="loading">Loading...</div>}

      {!loading && workouts.length === 0 && (
        <div className="empty">
          {!hasWorkoutAccess
            ? <>
                <h3>Membership Required</h3>
                <p>Programming is reserved for active paid members. Start or upgrade your membership in the Profile tab when you're ready to train with the full app.</p>
              </>
            : <>
                <h3>{isFuture ? 'Nothing posted yet' : 'Rest day'}</h3>
                <p>{isFuture ? 'Check back when programming is posted.' : 'No workout posted for this day.'}</p>
              </>
          }
        </div>
      )}

      {!loading && visibleWorkouts.map(w => (
        <WorkoutCard
          key={w.id}
          workout={w}
          user={user}
          isCoach={isCoach}
          isFuture={isFuture}
          expanded={expandedId === w.id}
          onToggle={() => setExpandedId(expandedId === w.id ? null : w.id)}
          onOpenLogSection={payload => setLogModal(payload)}
          onLogSectionScore={logSectionScore}
          onToggleReaction={toggleReaction}
          onPrepare={() => setPrepare({ workout: w, movements: getStrengthMovements(w) })}
          onEdit={() => setEditing(w)}
          onDelete={() => deleteWorkout(w.id)}
          onAthleteClick={isCoach ? (id) => setAthletePanel(id) : null}
        />
      ))}

      {prepare && (
        <PrepareModal
          workout={prepare.workout}
          movements={prepare.movements}
          user={user}
          onClose={() => setPrepare(null)}
        />
      )}

      {editing && (
        <EditWorkout
          workout={editing}
          onSaved={() => { setEditing(null); fetchWorkouts(); showToast('Workout updated') }}
          onClose={() => setEditing(null)}
        />
      )}

      {toast && <div className="toast">{toast}</div>}

      {logModal && (
        <SectionLogModal
          section={logModal.section}
          workout={logModal.workout}
          userId={user.id}
          onClose={() => setLogModal(null)}
          onSave={async payload => {
            const saved = await logSection(logModal.section, logModal.workout.id, payload)
            if (saved) setLogModal(null)
            return saved
          }}
        />
      )}

      {athletePanel && (
        <AthletePanel
          athleteId={athletePanel}
          onClose={() => setAthletePanel(null)}
          onUpdated={fetchWorkouts}
        />
      )}
    </div>
  )
}

function WorkoutCard({ workout, user, isCoach, isFuture, expanded, onToggle, onOpenLogSection, onLogSectionScore, onToggleReaction, onPrepare, onEdit, onDelete, onAthleteClick }) {
  const [expandedAthlete, setExpandedAthlete] = useState(null)
  const [demoVideo, setDemoVideo] = useState(null)
  const sections = (workout.workout_sections || []).sort((a, b) => a.order_index - b.order_index)
  const legacyResults = workout.results || []

  return (
    <div className="workout-card">
      <div className="workout-header" onClick={onToggle}>
        <div>
          <div className="workout-title">{workout.title}</div>
          <div className="workout-meta" style={{ marginTop: '6px' }}>
            <span className={`track-badge ${TC[workout.track] || 'track-open'}`}>{workout.track}</span>
            {isFuture && <span className="future-badge">Upcoming</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isCoach && (
            <>
              <button className="btn-ghost" style={{ fontSize: '10px' }} onClick={e => { e.stopPropagation(); onEdit() }}>Edit</button>
              <button className="btn-ghost" style={{ fontSize: '10px', color: 'var(--rose)' }} onClick={e => { e.stopPropagation(); if (window.confirm('Delete this workout? This cannot be undone.')) onDelete() }}>Delete</button>
            </>
          )}
          <span style={{ color: 'var(--charcoal-light)', fontSize: '18px' }}>{expanded ? '−' : '+'}</span>
        </div>
      </div>

      {expanded && (
        <div>
          <div className="workout-body">
            {workout.notes && <p className="workout-notes">{workout.notes}</p>}
            {sections.map(sec => {
              const scoreType = sec.score_type || 'No Score'
              const mySecLog = (sec.section_logs || []).find(sl => sl.athlete_id === user.id)
              const movementLeaderboards = scoreType === 'Heaviest Set' ? buildMovementLeaderboards(sec) : []
              const sectionLeaderboard = scoreType !== 'No Score' && scoreType !== 'Heaviest Set' ? buildSectionLeaderboard(sec, scoreType) : []

              return (
                <div key={sec.id} className="section-block">
                  <div className="section-block-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{sec.type}</span>
                    {scoreType !== 'No Score' && (
                      <span style={{ fontSize: '10px', letterSpacing: '1px', color: 'var(--charcoal-light)', textTransform: 'uppercase', fontFamily: 'Lato, sans-serif' }}>{scoreType}</span>
                    )}
                  </div>
                  {sec.notes && <p className="section-block-notes">{sec.notes}</p>}

                  {/* Movements list */}
                  {(sec.movements || []).sort((a, b) => a.order_index - b.order_index).map((m, mi) => {
                    const sets = (m.sets || []).sort((a, b) => a.order_index - b.order_index)
                    return (
                      <div key={mi} className="movement-block">
                        <div className="movement-block-name" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>{m.name}</span>
                        {m.demo_url && (
                          <button onClick={() => setDemoVideo({ url: m.demo_url, title: m.name })}
                            style={{ background: 'rgba(162,92,107,0.2)', border: '1px solid var(--rose)', borderRadius: '2px', color: 'var(--rose-light)', fontSize: '11px', padding: '2px 8px', cursor: 'pointer', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                            ▶ Demo
                          </button>
                        )}
                      </div>
                        {m.notes && <div className="movement-notes-text">{m.notes}</div>}
                        {sets.length > 0 && <div style={{ fontSize: '13px', color: 'var(--charcoal-light)', marginBottom: '6px' }}>{summarizeSets(sets, m.scheme)}</div>}
                        {sets.map((st, si) => {
                          const myLog = (st.set_logs || []).find(sl => sl.athlete_id === user.id)
                          const isAccessoryPrescription = sec.type === 'Accessory' && sets.length === 1 && st.reps && !st.load && !st.rpe
                          return (
                            <div key={si} className="set-log-row">
                              {isAccessoryPrescription ? (
                                <span className="set-reps">{st.reps}</span>
                              ) : (
                                <>
                                  <span className="set-number">Set {st.set_number}</span>
                                  {st.reps && <span className="set-reps">{formatPrescriptionValue(st.reps, m.scheme)}</span>}
                                  {st.load && <span className="set-load">@ {st.load}</span>}
                                  {st.rpe && <span className="set-rpe">RPE {st.rpe}</span>}
                                </>
                              )}
                              {myLog?.value && (
                                <span style={{ marginLeft: 'auto', color: isMissValue(myLog.value) ? 'var(--rose-light)' : 'var(--moss-light)', fontSize: '13px', fontFamily: 'Cinzel, serif' }}>
                                  {displaySetLogValue(myLog.value)}
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}

                  <div className="section-log-action">
                    <button className="btn-sm" onClick={() => onOpenLogSection({ workout, section: sec })}>
                      {mySecLog || (sec.movements || []).some(m => (m.sets || []).some(st => (st.set_logs || []).some(log => log.athlete_id === user.id))) ? `Edit ${sec.type} Log` : `Log ${sec.type}`}
                    </button>
                    {mySecLog?.notes && <span className="section-log-note-preview">Notes: {mySecLog.notes}</span>}
                  </div>

                  {/* Movement-by-movement leaderboards for strength sections */}
                  {scoreType === 'Heaviest Set' && movementLeaderboards.map(board => (
                    <SectionLeaderboard
                      key={board.movementId}
                      entries={board.entries}
                      scoreType={board.title}
                      userId={user.id}
                      reactions={RX}
                      legacyResults={legacyResults}
                      onToggleReaction={onToggleReaction}
                      expandedAthlete={expandedAthlete}
                      setExpandedAthlete={setExpandedAthlete}
                      onAthleteClick={onAthleteClick}
                    />
                  ))}

                  {/* Per-section leaderboard for conditioning/scored sections */}
                  {scoreType !== 'No Score' && scoreType !== 'Heaviest Set' && sectionLeaderboard.length > 0 && (
                    <SectionLeaderboard
                      entries={sectionLeaderboard}
                      scoreType={scoreType}
                      userId={user.id}
                      reactions={RX}
                      legacyResults={legacyResults}
                      onToggleReaction={onToggleReaction}
                      expandedAthlete={expandedAthlete}
                      setExpandedAthlete={setExpandedAthlete}
                      onAthleteClick={onAthleteClick}
                    />
                  )}
                </div>
              )
            })}

            <WorkoutHighFives
              results={legacyResults}
              userId={user.id}
              onToggleReaction={onToggleReaction}
              onAthleteClick={onAthleteClick}
            />

            {/* Legacy results from old system */}
            {legacyResults.length > 0 && sections.every(s => !s.score_type || s.score_type === 'No Score') && (
              <div style={{ marginTop: '1rem' }}>
                <div className="lb-title">Previous Results</div>
                {[...legacyResults].sort((a, b) => {
                  const av = parseFloat(a.score), bv = parseFloat(b.score)
                  if (!isNaN(av) && !isNaN(bv)) return bv - av
                  return 0
                }).map((r, i) => {
                  const ini = (r.profiles?.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                  const rankClass = ['gold', 'silver', 'bronze'][i] || 'other'
                  const rankSym = ['1', '2', '3'][i] || String(i + 1)
                  return (
                    <div key={r.id} className="lb-row">
                      <span className={`lb-rank ${rankClass}`}>{rankSym}</span>
                      {r.profiles?.avatar_url ? <img src={r.profiles.avatar_url} className="lb-avatar" alt="" /> : <div className="lb-avatar-placeholder">{ini}</div>}
                      <span className="lb-name">{r.profiles?.name || 'Athlete'}{r.athlete_id === user.id && <span className="lb-you">you</span>}</span>
                      <span className="lb-score">{r.score}</span>
                      {r.note && <span className="lb-note">{r.note}</span>}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="log-section">
            <div className="log-header">
              <h4>Log your results above</h4>
              <button className="btn-moss" onClick={onPrepare}>Prepare</button>
            </div>
          </div>
        </div>
      )}
    {demoVideo && <VideoModal url={demoVideo.url} title={demoVideo.title} onClose={() => setDemoVideo(null)} />}
    </div>
  )
}

function WorkoutHighFives({ results, userId, onToggleReaction, onAthleteClick }) {
  const logged = (results || []).filter(result => result.athlete_id && result.profiles)
  if (!logged.length) return null

  return (
    <div className="workout-high-fives">
      <div className="lb-title">Athlete High Fives</div>
      {logged.map(result => {
        const highFives = (result.reactions || []).filter(reaction => reaction.type === 'highfive')
        const hasReacted = highFives.some(reaction => reaction.athlete_id === userId)
        const initials = (result.profiles?.name || '?').split(' ').map(name => name[0]).join('').toUpperCase().slice(0, 2)
        return (
          <div className="high-five-row" key={result.id}>
            {result.profiles?.avatar_url
              ? <img src={result.profiles.avatar_url} className="lb-avatar" alt="" />
              : <div className="lb-avatar-placeholder">{initials}</div>}
            <button className="high-five-athlete" onClick={() => onAthleteClick?.(result.athlete_id)}>{result.profiles?.name || 'Athlete'}</button>
            <button
              className={`reaction-btn high-five-button ${hasReacted ? 'reacted' : ''}`}
              onClick={() => onToggleReaction(result.id, 'highfive', hasReacted)}
              aria-label={`${hasReacted ? 'Remove' : 'Give'} high five to ${result.profiles?.name || 'athlete'}`}
            >
              ✋ <span>{highFives.length}</span>
            </button>
          </div>
        )
      })}
    </div>
  )
}

function summarizeSets(sets, prescriptionType = 'reps') {
  const count = sets.length
  const reps = [...new Set(sets.map(st => st.reps).filter(Boolean))]
  if (count === 1) return sets[0]?.reps ? formatPrescriptionValue(sets[0].reps, prescriptionType) : '1 set'
  if (reps.length === 1) return `${count}x${formatPrescriptionValue(reps[0], prescriptionType)}`
  return `${count} sets`
}

function SetLogModal({ movement, section, workout, userId, onClose, onSave }) {
  const sets = (movement.sets || []).sort((a, b) => a.order_index - b.order_index)
  const [rows, setRows] = useState(() => sets.map(st => {
    const existing = (st.set_logs || []).find(log => log.athlete_id === userId)
    return {
      setId: st.id,
      setNumber: st.set_number,
      reps: st.reps || '',
      load: st.load || '',
      rpe: st.rpe || '',
      value: displaySetLogValue(existing?.value || ''),
      made: !isMissValue(existing?.value || '')
    }
  }))
  const [saving, setSaving] = useState(false)

  const updateRow = (index, patch) => {
    setRows(current => current.map((row, i) => i === index ? { ...row, ...patch } : row))
  }

  const copyDown = (index) => {
    setRows(current => current.map((row, i) => i > index ? { ...row, value: current[index].value, made: current[index].made } : row))
  }

  const save = async () => {
    setSaving(true)
    const ok = await onSave(rows)
    if (!ok) setSaving(false)
  }

  return (
    <div className="modal-wrap" onClick={e => { if (e.target.className === 'modal-wrap') onClose() }}>
      <div className="modal">
        <div className="modal-head">
          <div>
            <div className="modal-title">Log Sets</div>
            <div className="modal-sub">{movement.name} · {summarizeSets(sets, movement.scheme)} · {workout.title}</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {section?.notes && (
            <p style={{ color: 'var(--charcoal-light)', fontSize: '13px', lineHeight: 1.6, marginBottom: '1rem', fontStyle: 'italic' }}>
              {section.notes}
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {rows.map((row, index) => (
              <div key={row.setId} style={{ background: 'rgba(245,240,232,0.03)', border: '1px solid var(--border)', borderRadius: '3px', padding: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr auto', gap: '8px', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'Cinzel, serif', color: 'var(--gold-light)', fontSize: '12px' }}>Set {row.setNumber}</div>
                    <div style={{ fontSize: '11px', color: 'var(--charcoal-light)', marginTop: '2px' }}>
                      {[row.reps && formatPrescriptionValue(row.reps, movement.scheme), row.load && `@ ${row.load}`, row.rpe && `RPE ${row.rpe}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={row.value}
                    onChange={e => updateRow(index, { value: e.target.value })}
                    placeholder="Weight / result"
                    autoFocus={index === 0}
                    style={{ width: '100%', background: 'rgba(245,240,232,0.06)', border: '1px solid var(--border)', borderRadius: '2px', padding: '8px 10px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '15px', outline: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => copyDown(index)}
                    disabled={index === rows.length - 1 || !row.value.trim()}
                    title="Copy this result to sets below"
                    style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--charcoal-light)', borderRadius: '2px', width: '34px', height: '34px', cursor: index === rows.length - 1 || !row.value.trim() ? 'not-allowed' : 'pointer', opacity: index === rows.length - 1 || !row.value.trim() ? 0.4 : 1 }}
                  >
                    ↓
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                  <button
                    type="button"
                    className={row.made ? 'btn-moss' : 'btn-ghost'}
                    onClick={() => updateRow(index, { made: true })}
                    style={{ fontSize: '10px' }}
                  >
                    Made
                  </button>
                  <button
                    type="button"
                    className={!row.made ? 'btn-sm' : 'btn-ghost'}
                    onClick={() => updateRow(index, { made: false })}
                    style={{ fontSize: '10px' }}
                  >
                    Miss
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save Sets'}
            </button>
            <button className="btn-ghost" onClick={onClose} style={{ width: '100%' }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Section-level score input (For Time, AMRAP, Max Reps, Max Distance)
function SectionLogInput({ scoreType, myLog, onSave }) {
  const [editing, setEditing] = useState(false)
  const [score, setScore] = useState(myLog?.score || '')
  const [rounds, setRounds] = useState(myLog?.rounds != null ? String(myLog.rounds) : '')
  const [reps, setReps] = useState(myLog?.reps != null ? String(myLog.reps) : '')

  useEffect(() => {
    setScore(myLog?.score || '')
    setRounds(myLog?.rounds != null ? String(myLog.rounds) : '')
    setReps(myLog?.reps != null ? String(myLog.reps) : '')
  }, [myLog])

  const displayValue = () => {
    if (scoreType === 'AMRAP') {
      const parts = []
      if (myLog?.rounds != null) parts.push(`${myLog.rounds} rounds`)
      if (myLog?.reps != null) parts.push(`+ ${myLog.reps} reps`)
      return parts.join(' ') || null
    }
    return myLog?.score || null
  }

  const handleSave = () => {
    if (scoreType === 'AMRAP') {
      onSave({ rounds: rounds ? parseInt(rounds) : null, reps: reps ? parseInt(reps) : null, score: null })
    } else {
      onSave({ score, rounds: null, reps: null })
    }
    setEditing(false)
  }

  const placeholder = scoreType === 'For Time' ? 'e.g. 12:34' :
    scoreType === 'Max Reps / Calories' ? 'e.g. 45 reps' :
    scoreType === 'Max Distance' ? 'e.g. 500m' : 'Score'

  const current = displayValue()

  if (!editing) {
    return (
      <div style={{ marginTop: '10px' }}>
        <button
          onClick={() => setEditing(true)}
          style={{ background: current ? 'rgba(200,169,106,0.1)' : 'transparent', border: '1px solid', borderColor: current ? 'var(--gold-dark)' : 'var(--border)', borderRadius: '2px', color: current ? 'var(--gold-light)' : 'var(--charcoal-light)', padding: '6px 14px', cursor: 'pointer', fontSize: current ? '14px' : '12px', fontFamily: current ? 'Cinzel, serif' : 'Lato, sans-serif', letterSpacing: current ? '1px' : '2px', textTransform: current ? 'none' : 'uppercase' }}
        >
          {current || `Log ${scoreType}`}
        </button>
      </div>
    )
  }

  return (
    <div style={{ marginTop: '10px', background: 'rgba(245,240,232,0.03)', border: '1px solid var(--border)', borderRadius: '2px', padding: '12px' }}>
      {scoreType === 'AMRAP'
        ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--charcoal-light)' }}>Rounds</label>
              <input autoFocus type="number" min="0" value={rounds} onChange={e => setRounds(e.target.value)} placeholder="0"
                style={{ width: '70px', background: 'rgba(245,240,232,0.06)', border: '1px solid var(--gold)', borderRadius: '2px', padding: '6px 8px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '15px', outline: 'none' }} />
            </div>
            <div style={{ fontSize: '16px', color: 'var(--charcoal-light)', marginTop: '18px' }}>+</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--charcoal-light)' }}>Reps</label>
              <input type="number" min="0" value={reps} onChange={e => setReps(e.target.value)} placeholder="0"
                style={{ width: '70px', background: 'rgba(245,240,232,0.06)', border: '1px solid var(--gold)', borderRadius: '2px', padding: '6px 8px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '15px', outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '18px' }}>
              <button onClick={handleSave} className="btn-sm" style={{ padding: '6px 14px' }}>Save</button>
              <button onClick={() => setEditing(false)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        )
        : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input autoFocus type="text" value={score} onChange={e => setScore(e.target.value)} placeholder={placeholder}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
              style={{ flex: 1, background: 'rgba(245,240,232,0.06)', border: '1px solid var(--gold)', borderRadius: '2px', padding: '6px 10px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '15px', outline: 'none' }} />
            <button onClick={handleSave} className="btn-sm" style={{ padding: '6px 14px' }}>Save</button>
            <button onClick={() => setEditing(false)} className="btn-ghost">Cancel</button>
          </div>
        )
      }
    </div>
  )
}

// Notes/scaling field - always visible on every section
function SectionNotesInput({ myLog, onSave }) {
  const [editing, setEditing] = useState(false)
  const [notes, setNotes] = useState(myLog?.notes || '')

  useEffect(() => { setNotes(myLog?.notes || '') }, [myLog])

  if (!editing) {
    return (
      <div style={{ marginTop: '8px' }}>
        <button onClick={() => setEditing(true)}
          style={{ background: 'transparent', border: 'none', color: myLog?.notes ? 'var(--moss-light)' : 'var(--charcoal-light)', cursor: 'pointer', fontSize: '12px', letterSpacing: '1px', padding: '2px 0', textAlign: 'left' }}>
          {myLog?.notes ? `📝 ${myLog.notes}` : '+ Add scaling / notes'}
        </button>
      </div>
    )
  }

  return (
    <div style={{ marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
      <input autoFocus type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Scaling, notes, how it felt..."
        onKeyDown={e => { if (e.key === 'Enter') { onSave(notes); setEditing(false) } if (e.key === 'Escape') setEditing(false) }}
        style={{ flex: 1, background: 'rgba(245,240,232,0.06)', border: '1px solid var(--border)', borderRadius: '2px', padding: '5px 8px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '13px', outline: 'none' }} />
      <button onClick={() => { onSave(notes); setEditing(false) }} className="btn-sm" style={{ padding: '4px 10px', fontSize: '11px' }}>Save</button>
      <button onClick={() => setEditing(false)} className="btn-ghost" style={{ padding: '4px 10px', fontSize: '11px' }}>✕</button>
    </div>
  )
}

// Per-set log input for Heaviest Set
function SetLogInput({ value, scoreType, onSave }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value)

  useEffect(() => { setVal(value) }, [value])

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)}
        style={{ marginLeft: 'auto', background: val ? 'rgba(200,169,106,0.1)' : 'transparent', border: '1px solid', borderColor: val ? 'var(--gold-dark)' : 'var(--border)', borderRadius: '2px', color: val ? 'var(--gold-light)' : 'var(--charcoal-light)', padding: '3px 10px', cursor: 'pointer', fontSize: '13px', fontFamily: val ? 'Cinzel, serif' : 'Lato, sans-serif', whiteSpace: 'nowrap', minWidth: '60px', textAlign: 'center' }}>
        {val || 'Log'}
      </button>
    )
  }

  return (
    <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto', alignItems: 'center' }}>
      <input autoFocus type="text" value={val} onChange={e => setVal(e.target.value)} placeholder="lbs/kg"
        onKeyDown={e => { if (e.key === 'Enter') { onSave(val); setEditing(false) } if (e.key === 'Escape') setEditing(false) }}
        style={{ width: '80px', background: 'rgba(245,240,232,0.06)', border: '1px solid var(--gold)', borderRadius: '2px', padding: '4px 8px', color: 'var(--bone)', fontFamily: 'Lato, sans-serif', fontSize: '13px', outline: 'none' }} />
      <button onClick={() => { onSave(val); setEditing(false) }} className="btn-sm" style={{ padding: '4px 8px', fontSize: '11px' }}>✓</button>
    </div>
  )
}

function SectionLeaderboard({ entries, scoreType, userId, reactions, legacyResults, onToggleReaction, expandedAthlete, setExpandedAthlete, onAthleteClick }) {
  const [openPicker, setOpenPicker] = useState(null)

  return (
    <div className="leaderboard" style={{ marginTop: '12px' }}>
      <div className="lb-title">{scoreType}</div>
      {entries.map((entry, i) => {
        const rankClass = ['gold', 'silver', 'bronze'][i] || 'other'
        const rankSym = ['1', '2', '3'][i] || String(i + 1)
        const isMe = entry.athleteId === userId
        const entryKey = entry.entryKey || `${entry.athleteId}-${entry.sectionId}`
        const isExpanded = expandedAthlete === entryKey
        const result = legacyResults.find(r => r.athlete_id === entry.athleteId)
        const allReactions = result?.reactions || []
        const totalReactions = allReactions.length
        const myReaction = reactions.find(rx => allReactions.some(x => x.type === rx.k && x.athlete_id === userId))
        const pickerOpen = openPicker === entryKey

        return (
          <div key={entryKey}>
            <div className="lb-row" style={{ cursor: 'pointer', position: 'relative' }} onClick={() => setExpandedAthlete(isExpanded ? null : entryKey)}>
              <span className={`lb-rank ${rankClass}`}>{rankSym}</span>
              {entry.avatarUrl ? <img src={entry.avatarUrl} className="lb-avatar" alt="" /> : <div className="lb-avatar-placeholder">{(entry.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</div>}
              <span className="lb-name"
                onClick={e => { e.stopPropagation(); if (onAthleteClick) onAthleteClick(entry.athleteId) }}
                style={onAthleteClick ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--border)' } : {}}
              >{entry.name}{isMe && <span className="lb-you">you</span>}</span>
              <span className="lb-score">{entry.displayScore}</span>
              {entry.notes && <span className="lb-note" title={entry.notes}>📝</span>}
              <span style={{ fontSize: '11px', color: 'var(--charcoal-light)' }}>{isExpanded ? '▲' : '▼'}</span>

              {result && (
                <div className="lb-reactions" style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                  <button
                    className={`reaction-btn ${myReaction ? 'reacted' : ''}`}
                    onClick={() => setOpenPicker(pickerOpen ? null : entryKey)}
                    title="React"
                  >
                    {myReaction ? myReaction.e : '✨'}{totalReactions > 0 && <span>{totalReactions}</span>}
                  </button>

                  {pickerOpen && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      top: '28px',
                      zIndex: 20,
                      display: 'flex',
                      gap: '4px',
                      padding: '6px',
                      background: 'rgba(13,13,13,0.98)',
                      border: '1px solid var(--gold-dark)',
                      borderRadius: '4px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
                    }}>
                      {reactions.map(rx => {
                        const rxArr = allReactions.filter(x => x.type === rx.k)
                        const hasReacted = rxArr.some(x => x.athlete_id === userId)
                        return (
                          <button
                            key={rx.k}
                            className={`reaction-btn ${hasReacted ? 'reacted' : ''}`}
                            onClick={() => {
                              onToggleReaction(result.id, rx.k, hasReacted)
                              setOpenPicker(null)
                            }}
                            title={rx.k}
                          >
                            {rx.e}{rxArr.length > 0 && <span>{rxArr.length}</span>}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {isExpanded && (
              <div style={{ paddingLeft: '56px', paddingBottom: '10px', borderBottom: '1px solid var(--border)' }}>
                {entry.sets.map((s, si) => (
                  <div key={si} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '3px 0', fontSize: '13px' }}>
                    <span style={{ color: 'var(--charcoal-light)', fontFamily: 'Cinzel, serif', fontSize: '11px', minWidth: '44px' }}>{s.movName} S{s.setNumber}</span>
                    {s.reps && <span style={{ color: 'var(--bone)' }}>{formatPrescriptionValue(s.reps, s.prescriptionType)}</span>}
                    {s.load && <span style={{ color: 'var(--charcoal-light)', fontSize: '12px' }}>@ {s.load}</span>}
                    <span style={{ color: 'var(--gold-light)', fontFamily: 'Cinzel, serif', marginLeft: 'auto' }}>{s.logged}</span>
                  </div>
                ))}
                {entry.notes && (
                  <div style={{ fontSize: '12px', color: 'var(--moss-light)', marginTop: '6px', fontStyle: 'italic' }}>📝 {entry.notes}</div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function buildMovementLeaderboards(section) {
  return (section.movements || [])
    .sort((a, b) => a.order_index - b.order_index)
    .map(mov => {
      const athleteMap = {}

      ;(mov.sets || []).forEach(st => {
        ;(st.set_logs || []).forEach(sl => {
          if (!sl.value) return
          if (!athleteMap[sl.athlete_id]) {
            athleteMap[sl.athlete_id] = {
              athleteId: sl.athlete_id,
              sectionId: section.id,
              movementId: mov.id,
              entryKey: `${sl.athlete_id}-${section.id}-${mov.id}`,
              name: sl.profiles?.name || 'Athlete',
              avatarUrl: sl.profiles?.avatar_url || null,
              allValues: [],
              sets: [],
              notes: null
            }
          }
          athleteMap[sl.athlete_id].allValues.push(sl.value)
          athleteMap[sl.athlete_id].sets.push({
            movName: mov.name,
            prescriptionType: mov.scheme,
            setNumber: st.set_number,
            reps: st.reps,
            load: st.load,
            logged: sl.value
          })
        })
      })

      const entries = Object.values(athleteMap)
        .map(a => {
          const bestScore = getBestScore(a.allValues, 'Heaviest Set')
          return {
            ...a,
            displayScore: bestScore,
            sortScore: parseScore(bestScore, 'Heaviest Set')
          }
        })
        .filter(a => a.displayScore !== null)
        .sort((a, b) => b.sortScore - a.sortScore)

      return { movementId: mov.id, title: mov.name, entries }
    })
    .filter(board => board.entries.length > 0)
}

function buildSectionLeaderboard(section, scoreType) {
  const athleteMap = {}

  if (scoreType === 'Heaviest Set') {
    // Build from set_logs
    ;(section.movements || []).forEach(mov => {
      (mov.sets || []).forEach(st => {
        (st.set_logs || []).forEach(sl => {
          if (!sl.value) return
          if (!athleteMap[sl.athlete_id]) {
            athleteMap[sl.athlete_id] = {
              athleteId: sl.athlete_id,
              sectionId: section.id,
              name: sl.profiles?.name || 'Athlete',
              avatarUrl: sl.profiles?.avatar_url || null,
              allValues: [],
              sets: [],
              notes: null
            }
          }
          athleteMap[sl.athlete_id].allValues.push(sl.value)
          athleteMap[sl.athlete_id].sets.push({
            movName: mov.name,
            prescriptionType: mov.scheme,
            setNumber: st.set_number,
            reps: st.reps,
            load: st.load,
            logged: sl.value
          })
        })
      })
    })

    return Object.values(athleteMap)
      .map(a => ({
        ...a,
        displayScore: getBestScore(a.allValues, scoreType),
        sortScore: parseScore(getBestScore(a.allValues, scoreType), scoreType)
      }))
      .filter(a => a.displayScore !== null)
      .sort((a, b) => b.sortScore - a.sortScore)
  }

  // Build from section_logs for all other score types
  ;(section.section_logs || []).forEach(sl => {
    if (!athleteMap[sl.athlete_id]) {
      athleteMap[sl.athlete_id] = {
        athleteId: sl.athlete_id,
        sectionId: section.id,
        name: sl.profiles?.name || 'Athlete',
        avatarUrl: sl.profiles?.avatar_url || null,
        log: sl,
        sets: [],
        notes: sl.notes || null
      }
    }
  })

  return Object.values(athleteMap)
    .map(a => {
      const display = formatSectionScore(a.log, scoreType)
      const sortVal = sectionScoreForSort(a.log, scoreType)
      return { ...a, displayScore: display, sortScore: sortVal }
    })
    .filter(a => a.displayScore !== null)
    .sort((a, b) => {
      if (scoreType === 'For Time') {
        return parseScore(a.displayScore, scoreType) - parseScore(b.displayScore, scoreType)
      }
      if (scoreType === 'AMRAP') {
        return (b.sortScore || '').localeCompare(a.sortScore || '')
      }
      return parseScore(b.displayScore, scoreType) - parseScore(a.displayScore, scoreType)
    })
}
